import React, { useEffect, useState } from "react";
import axios from "axios";
import "../css/GeoDatasetComponent.css";
import { useNavigate } from 'react-router-dom';
import { FaArrowLeft } from 'react-icons/fa';

const ITEMS_PER_PAGE = 50;

/* =======================
   Static lineage (Eragrostis tef)
======================= */
const ERAGROSTIS_TEF_LINEAGE = [
  "cellular organisms", "Eukaryota", "Viridiplantae", "Streptophyta",
  "Streptophytina", "Embryophyta", "Tracheophyta", "Euphyllophyta",
  "Spermatophyta", "Magnoliopsida", "Mesangiospermae", "Liliopsida",
  "Petrosaviidae", "commelinids", "Poales", "Poaceae", "PACMAD clade",
  "Chloridoideae", "Eragrostideae", "Eragrostidinae", "Eragrostis"
];

const GeoDatasetComponent = () => {
  const [geoData, setGeoData] = useState({ series: [], platforms: [], samples: [] });
  const [counts, setCounts] = useState({ series_count: 0, platform_count: 0, sample_count: 0 });
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);
  const [fullViewItem, setFullViewItem] = useState(null);
  const [fullViewType, setFullViewType] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    axios.get("http://197.156.76.122/api/geodata/")
      .then(res => {
        const series = [], platforms = [], samples = [];
        res.data.forEach(item => {
          if (item.type === "series") series.push(item);
          else if (item.type === "platform") platforms.push(item);
          else if (item.type === "sample") samples.push(item);
        });
        setGeoData({ series, platforms, samples });
      })
      .catch(() => setError("Failed to load GEO data."));

    axios.get("http://197.156.76.122/api/geodata/counts/")
      .then(res => setCounts(res.data))
      .catch(() => setError("Failed to load GEO counts."));
  }, []);

  if (error) return <div className="geo-error">{error}</div>;

  const filteredItems = filter === "series" ? geoData.series
                      : filter === "platform" ? geoData.platforms
                      : filter === "samples" ? geoData.samples
                      : [...geoData.series, ...geoData.platforms, ...geoData.samples];

  const paginatedItems = filteredItems.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  );

  if (fullViewItem) {
    return (
      <div className="geo-full-view-page">
        {/* 🔙 Back to Home */}
        <button className="geo-back-home-btn" onClick={() => navigate('/')}>
          <FaArrowLeft /> Back to Home
        </button>

        {/* Back to list results */}
        <button className="geo-back-btn" onClick={() => setFullViewItem(null)}>
          ← Back to Main Page
        </button>

        <GeoFullView item={fullViewItem} type={fullViewType} />
      </div>
    );
  }

  return (
    <div className="geo-layout">
      {/* 🔙 Back to Home */}
      <button className="geo-back-home-btn" onClick={() => navigate('/')}>
        <FaArrowLeft /> Back to Home
      </button>

      <h2 className="geo-title">Gene Expression Omnibus (GEO) Dataset Results</h2>

      <div className="geo-filter-panel">
        <button className={filter === "all" ? "active" : ""} onClick={() => setFilter("all")}>
          All ({counts.series_count + counts.platform_count + counts.sample_count})
        </button>
        <button className={filter === "series" ? "active" : ""} onClick={() => setFilter("series")}>
          Series ({counts.series_count})
        </button>
        <button className={filter === "platform" ? "active" : ""} onClick={() => setFilter("platform")}>
          Platforms ({counts.platform_count})
        </button>
        <button className={filter === "samples" ? "active" : ""} onClick={() => setFilter("samples")}>
          Samples ({counts.sample_count})
        </button>
      </div>

      <div className="geo-container">
        <Pagination currentPage={currentPage} totalItems={filteredItems.length} setPage={setCurrentPage} />
        {paginatedItems.map((item, idx) => (
          <GeoItem
            key={item.id}
            idx={(currentPage - 1) * ITEMS_PER_PAGE + idx + 1}
            item={item}
            onFullView={() => { setFullViewItem(item); setFullViewType(item.type); }}
          />
        ))}
        <Pagination currentPage={currentPage} totalItems={filteredItems.length} setPage={setCurrentPage} />
      </div>
    </div>
  );
};

/* =======================
   GEO Item Card (Main Page)
======================= */
const GeoItem = ({ idx, item, onFullView }) => {
  const geoUrl = (acc) => `https://www.ncbi.nlm.nih.gov/geo/query/acc.cgi?acc=${acc}`;
  return (
    <div className="geo-item">
      <div className="geo-item-header">
        <div className="geo-item-index">{idx}.</div>
        <div className="geo-item-title" onClick={onFullView}>{item.title}</div>
      </div>
      {item.description && <div className="geo-item-description">{item.description}</div>}
      <div className="geo-metadata-card">
        {item.organism && (
          <div className="geo-metadata-row">
            <span className="geo-label">Organism:</span> {item.organism}
            <div className="geo-lineage">
              {ERAGROSTIS_TEF_LINEAGE.map((taxon, i) => (
                <span key={i}>{taxon}{i < ERAGROSTIS_TEF_LINEAGE.length - 1 && " › "}</span>
              ))}
            </div>
          </div>
        )}
        {item.type && <div className="geo-metadata-row"><span className="geo-label">Type:</span> {item.type}</div>}
      </div>
      <div className="geo-download">
        <a href={geoUrl(item.accession)} target="_blank" rel="noopener noreferrer">Go to NCBI {item.type}</a>
      </div>
    </div>
  );
};

/* =======================
   Full View - All Attributes + Copy/Download + Metadata Card
======================= */
const GeoFullView = ({ item, type }) => {
  const geoUrl = (acc) => `https://www.ncbi.nlm.nih.gov/geo/query/acc.cgi?acc=${acc}`;
  const [copied, setCopied] = useState(false);

  const jsonData = JSON.stringify(item, null, 2);

  const copyToClipboard = () => {
    navigator.clipboard.writeText(jsonData)
      .then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      })
      .catch(() => alert("Failed to copy to clipboard"));
  };

  const downloadJSON = () => {
    const blob = new Blob([jsonData], { type: "application/json" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `${item.accession}.json`;
    link.click();
  };

  return (
    <div className="geo-full-view">
      <h2>{item.title}</h2>

      <div className="geo-actions">
        <button onClick={copyToClipboard}>
          {copied ? "Copied!" : "Copy JSON"}
        </button>
        <button onClick={downloadJSON}>Download JSON</button>
      </div>

      <div className="geo-metadata-card-full">
        {item.organism && (
          <div>
            <strong>Organism:</strong> {item.organism}
            <div className="geo-lineage">
              {ERAGROSTIS_TEF_LINEAGE.map((taxon, i) => (
                <span key={i}>{taxon}{i < ERAGROSTIS_TEF_LINEAGE.length - 1 && " › "}</span>
              ))}
            </div>
          </div>
        )}
        {item.type && <div><strong>Type:</strong> {item.type}</div>}
        {item.parent_id && <div><strong>Parent ID:</strong> {item.parent_id}</div>}
        {item.dataset_id && <div><strong>Dataset ID:</strong> {item.dataset_id}</div>}
        {item.sra_run_selector && (
          <div>
            <strong>SRA Run Selector:</strong>{" "}
            <a href={item.sra_run_selector} target="_blank" rel="noopener noreferrer">{item.sra_run_selector}</a>
          </div>
        )}
        {item.ftp_link && (
          <div>
            <strong>FTP Link:</strong>{" "}
            <a href={item.ftp_link} target="_blank" rel="noopener noreferrer">{item.ftp_link}</a>
          </div>
        )}
        {item.description && <div><strong>Data Processing:</strong> {item.description}</div>}
      </div>

      {item.additional_metadata && (
        <div className="geo-additional-metadata-card">
          <strong>Additional Metadata:</strong>
          <pre>{JSON.stringify(item.additional_metadata, null, 2)}</pre>
        </div>
      )}

      <a href={geoUrl(item.accession)} target="_blank" rel="noopener noreferrer">
        Go to NCBI {type}
      </a>
    </div>
  );
};

/* =======================
   Pagination
======================= */
const Pagination = ({ currentPage, totalItems, setPage }) => {
  const totalPages = Math.ceil(totalItems / ITEMS_PER_PAGE);
  if (totalPages <= 1) return null;

  return (
    <div className="geo-pagination">
      <button onClick={() => setPage(currentPage - 1)} disabled={currentPage === 1}>Previous</button>
      <span>Page {currentPage} of {totalPages}</span>
      <button onClick={() => setPage(currentPage + 1)} disabled={currentPage === totalPages}>Next</button>
    </div>
  );
};

export default GeoDatasetComponent;
